import type { Position } from 'geojson';

import type {
  FeatureProperties,
  JsonValue,
  SupportedGeometry,
  VectorFeature,
  VectorFeatureCollection,
} from '../model';

export type VectorFeatureValidationErrorCode =
  'coordinate-out-of-range' | 'invalid-coordinate' | 'invalid-data' | 'unsupported-geometry';

export class VectorFeatureValidationError extends Error {
  constructor(
    readonly code: VectorFeatureValidationErrorCode,
    message: string,
  ) {
    super(message);
  }
}

export type CoordinateTransform = (position: readonly number[], path: string) => Position;

export interface VectorFeatureValidationOptions {
  readonly allowBareGeoJson?: boolean;
  readonly coordinateTransform?: CoordinateTransform;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function invalidData(message: string): never {
  throw new VectorFeatureValidationError('invalid-data', message);
}

function unsupportedGeometry(message: string): never {
  throw new VectorFeatureValidationError('unsupported-geometry', message);
}

function invalidCoordinate(message: string): never {
  throw new VectorFeatureValidationError('invalid-coordinate', message);
}

function requireArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) invalidData(`${path} must be an array.`);
  return value;
}

function validateNumericPosition(value: unknown, path: string): number[] {
  if (!Array.isArray(value)) {
    invalidCoordinate(`${path} must be an array containing x and y coordinates.`);
  }
  const position = value;
  if (position.length < 2) {
    invalidCoordinate(`${path} must contain at least x and y coordinates.`);
  }

  for (const coordinate of position) {
    if (typeof coordinate !== 'number' || !Number.isFinite(coordinate)) {
      invalidCoordinate(`${path} contains a non-finite or non-numeric coordinate.`);
    }
  }

  return position as number[];
}

function parsePosition(
  value: unknown,
  path: string,
  coordinateTransform?: CoordinateTransform,
): Position {
  const sourcePosition = validateNumericPosition(value, path);
  const position = coordinateTransform
    ? validateNumericPosition(coordinateTransform(sourcePosition, path), path)
    : sourcePosition;
  const longitude = position[0];
  const latitude = position[1];

  if (
    longitude === undefined ||
    latitude === undefined ||
    longitude < -180 ||
    longitude > 180 ||
    latitude < -90 ||
    latitude > 90
  ) {
    throw new VectorFeatureValidationError(
      'coordinate-out-of-range',
      `${path} is outside the WGS84 longitude/latitude range.`,
    );
  }

  return position;
}

function parsePositions(
  value: unknown,
  path: string,
  coordinateTransform?: CoordinateTransform,
): Position[] {
  return requireArray(value, path).map((position, index) =>
    parsePosition(position, `${path}[${index}]`, coordinateTransform),
  );
}

function validateLine(positions: Position[], path: string): Position[] {
  if (positions.length === 1) {
    invalidCoordinate(`${path} must be empty or contain at least two positions.`);
  }
  return positions;
}

function positionsMatch(first: Position, last: Position): boolean {
  return first.length === last.length && first.every((value, index) => value === last[index]);
}

function validateRing(positions: Position[], path: string): Position[] {
  if (positions.length < 4) invalidCoordinate(`${path} must contain at least four positions.`);

  const first = positions[0];
  const last = positions.at(-1);
  if (!first || !last || !positionsMatch(first, last)) {
    invalidCoordinate(`${path} must be closed by repeating its first position.`);
  }
  return positions;
}

function parseLines(
  value: unknown,
  path: string,
  coordinateTransform?: CoordinateTransform,
): Position[][] {
  return requireArray(value, path).map((line, index) =>
    validateLine(
      parsePositions(line, `${path}[${index}]`, coordinateTransform),
      `${path}[${index}]`,
    ),
  );
}

function parsePolygonCoordinates(
  value: unknown,
  path: string,
  coordinateTransform?: CoordinateTransform,
): Position[][] {
  return requireArray(value, path).map((ring, index) =>
    validateRing(
      parsePositions(ring, `${path}[${index}]`, coordinateTransform),
      `${path}[${index}]`,
    ),
  );
}

function parseGeometry(
  value: unknown,
  path: string,
  coordinateTransform?: CoordinateTransform,
): SupportedGeometry {
  if (!isRecord(value) || typeof value.type !== 'string') {
    invalidData(`${path} must be a GeoJSON geometry object.`);
  }

  switch (value.type) {
    case 'Point':
      return {
        type: 'Point',
        coordinates: parsePosition(value.coordinates, `${path}.coordinates`, coordinateTransform),
      };
    case 'MultiPoint':
      return {
        type: 'MultiPoint',
        coordinates: parsePositions(value.coordinates, `${path}.coordinates`, coordinateTransform),
      };
    case 'LineString':
      return {
        type: 'LineString',
        coordinates: validateLine(
          parsePositions(value.coordinates, `${path}.coordinates`, coordinateTransform),
          `${path}.coordinates`,
        ),
      };
    case 'MultiLineString':
      return {
        type: 'MultiLineString',
        coordinates: parseLines(value.coordinates, `${path}.coordinates`, coordinateTransform),
      };
    case 'Polygon':
      return {
        type: 'Polygon',
        coordinates: parsePolygonCoordinates(
          value.coordinates,
          `${path}.coordinates`,
          coordinateTransform,
        ),
      };
    case 'MultiPolygon':
      return {
        type: 'MultiPolygon',
        coordinates: requireArray(value.coordinates, `${path}.coordinates`).map((polygon, index) =>
          parsePolygonCoordinates(polygon, `${path}.coordinates[${index}]`, coordinateTransform),
        ),
      };
    case 'GeometryCollection':
      return unsupportedGeometry(
        'Placeio Maps does not currently support GeometryCollection. Convert it to point, line, or polygon features and try again.',
      );
    default:
      return unsupportedGeometry(`Geometry type “${value.type}” is not supported by Placeio Maps.`);
  }
}

function normalizeJsonValue(value: unknown, path: string): JsonValue {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) invalidData(`${path} contains a non-finite number.`);
    return value;
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.valueOf())) invalidData(`${path} contains an invalid date.`);
    const year = String(value.getFullYear()).padStart(4, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (Array.isArray(value)) {
    return value.map((item, index) => normalizeJsonValue(item, `${path}[${index}]`));
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([name, item]) => [
        name,
        normalizeJsonValue(item, `${path}.${name}`),
      ]),
    );
  }
  return invalidData(`${path} contains a value that cannot be stored as JSON.`);
}

function parseProperties(value: unknown, path: string): FeatureProperties {
  if (value === null) return null;
  if (!isRecord(value)) invalidData(`${path} must be an object or null.`);

  return Object.fromEntries(
    Object.entries(value).map(([name, item]) => [
      name,
      normalizeJsonValue(item, `${path}.${name}`),
    ]),
  );
}

function parseFeature(
  value: unknown,
  path: string,
  coordinateTransform?: CoordinateTransform,
): VectorFeature {
  if (!isRecord(value) || value.type !== 'Feature') {
    invalidData(`${path} must be a GeoJSON Feature.`);
  }
  if (!Object.hasOwn(value, 'geometry')) invalidData(`${path}.geometry is required.`);

  const geometry =
    value.geometry === null
      ? null
      : parseGeometry(value.geometry, `${path}.geometry`, coordinateTransform);
  // Some common exporters omit the optional payload when a feature has no
  // attributes. Canonicalise that real-world form to GeoJSON's null value.
  const properties = parseProperties(
    Object.hasOwn(value, 'properties') ? value.properties : null,
    `${path}.properties`,
  );
  const id = value.id;

  if (id !== undefined && typeof id !== 'string' && typeof id !== 'number') {
    invalidData(`${path}.id must be a string or number when provided.`);
  }

  return {
    type: 'Feature',
    geometry,
    properties,
    ...(id === undefined ? {} : { id }),
  };
}

export function parseCanonicalVectorData(
  value: unknown,
  options: VectorFeatureValidationOptions = {},
): VectorFeatureCollection {
  if (!isRecord(value) || typeof value.type !== 'string') {
    invalidData('The top-level value must be a GeoJSON object.');
  }

  if (value.type === 'FeatureCollection') {
    const features = requireArray(value.features, 'features').map((feature, index) =>
      parseFeature(feature, `features[${index}]`, options.coordinateTransform),
    );
    return { type: 'FeatureCollection', features };
  }

  if (!options.allowBareGeoJson) {
    invalidData('The Shapefile parser did not return a FeatureCollection.');
  }

  if (value.type === 'Feature') {
    return {
      type: 'FeatureCollection',
      features: [parseFeature(value, 'feature', options.coordinateTransform)],
    };
  }

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: parseGeometry(value, 'geometry', options.coordinateTransform),
        properties: null,
      },
    ],
  };
}
